import type { PipelineStage } from '../types';
import { draftText } from '../draft-text';

const CODE_FENCE_RE = /```[\w-]*\n([\s\S]*?)```/g;
const MIN_DUP_CHARS = 40; // ignore trivial/short blocks — false-positive risk isn't worth it

interface DuplicateBlock {
  full: string;
  inner: string;
}

function findDuplicateFencedBlocks(userMessage: string, haystack: string): DuplicateBlock[] {
  const dups: DuplicateBlock[] = [];
  for (const m of userMessage.matchAll(CODE_FENCE_RE)) {
    const inner = m[1].trim();
    if (inner.length >= MIN_DUP_CHARS && haystack.includes(inner)) dups.push({ full: m[0], inner });
  }
  return dups;
}

export const e1DuplicateContentStripping: PipelineStage = {
  id: 'E1',
  group: 'E',
  label: 'Duplicate content stripping',
  description: 'Strip pasted content that duplicates what is already present in context or recent history.',
  impact: 'Medium',
  defaultEnabled: true,
  implemented: true,
  params: {
    // Off by default: per the guardrail, nothing in the user's literal typed message is ever
    // changed without their explicit, standing opt-in — this toggle *is* that confirmation.
    // With it off, duplicates are only surfaced in the pre-send inspector, never removed.
    autoStripConfirmedDuplicates: { type: 'boolean', default: false, label: 'Auto-strip confirmed duplicates' },
  },

  async run(draft, params, ctx) {
    const tokensBefore = ctx.estimateTokens(draftText(draft));

    const haystack = [...draft.contextBlocks.map((b) => b.content), ...draft.history.map((t) => t.content)].join(
      '\n',
    );
    const dups = findDuplicateFencedBlocks(draft.userMessage, haystack);

    if (dups.length === 0) {
      return {
        draft,
        report: {
          stageId: 'E1',
          tokensBefore,
          tokensAfter: tokensBefore,
          tokensSaved: 0,
          notes: ['No pasted content in the message duplicates what is already in context/history.'],
        },
      };
    }

    if (!params.autoStripConfirmedDuplicates) {
      return {
        draft,
        report: {
          stageId: 'E1',
          tokensBefore,
          tokensAfter: tokensBefore,
          tokensSaved: 0,
          notes: [
            `Found ${dups.length} pasted code block(s) that duplicate content already included elsewhere. ` +
              'Nothing was changed — enable "Auto-strip confirmed duplicates" to remove them automatically.',
          ],
        },
      };
    }

    let newMessage = draft.userMessage;
    for (const d of dups) {
      newMessage = newMessage.replace(
        d.full,
        '```\n[omitted — identical to content already included in context/history above]\n```',
      );
    }

    const newDraft = { ...draft, userMessage: newMessage };
    const tokensAfter = ctx.estimateTokens(draftText(newDraft));

    return {
      draft: newDraft,
      report: {
        stageId: 'E1',
        tokensBefore,
        tokensAfter,
        tokensSaved: Math.max(0, tokensBefore - tokensAfter),
        notes: [`Stripped ${dups.length} duplicate pasted code block(s) from your message (auto-strip enabled).`],
      },
    };
  },
};
